import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, MessageCircle, MapPin, Send, User, Search, Clock, Building } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  position?: string;
  residence_country?: string;
}

interface Employee {
  id: string;
  profile_id: string;
  current_country?: string;
  profiles: Profile;
}

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  message: string;
  read: boolean;
  created_at: string;
  sender_profile: Profile;
}

interface CommunityGroup {
  id: string;
  name: string;
  destination: string;
  memberCount: number;
  isJoined: boolean;
}

interface MockConversation {
  id: string;
  name: string;
  company: string;
  location: string;
  duration: string;
  lastMessage: string;
  timestamp: string;
  isHR?: boolean;
  isManager?: boolean;
  avatar: string;
}

interface CommunityTabProps {
  currentProfile: Profile | null;
}

const CommunityTab = ({ currentProfile }: CommunityTabProps) => {
  const [colleagues, setColleagues] = useState<Employee[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedColleague, setSelectedColleague] = useState<Employee | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [communityGroups, setCommunityGroups] = useState<CommunityGroup[]>([]);
  const [mockConversations, setMockConversations] = useState<MockConversation[]>([]);
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (currentProfile) {
      fetchCurrentEmployee();
      fetchColleagues();
      fetchMessages();
      loadMockData();
    }
  }, [currentProfile]);

  const fetchCurrentEmployee = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          profile_id,
          current_country,
          company_id,
          profiles!inner (
            id,
            first_name,
            last_name,
            position,
            residence_country
          )
        `)
        .eq('profile_id', currentProfile?.id)
        .single();

      if (error) throw error;
      setCurrentEmployee(data);
    } catch (error: any) {
      console.error('Error fetching current employee:', error);
    }
  };

  const fetchColleagues = async () => {
    try {
      // Get current employee to find company_id
      const { data: currentEmployeeData } = await supabase
        .from('employees')
        .select('company_id')
        .eq('profile_id', currentProfile?.id)
        .single();

      if (!currentEmployeeData) return;

      // Get all colleagues from the same company
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          profile_id,
          current_country,
          profiles!inner (
            id,
            first_name,
            last_name,
            position,
            residence_country
          )
        `)
        .eq('company_id', currentEmployeeData.company_id)
        .neq('profile_id', currentProfile?.id);

      if (error) throw error;
      setColleagues(data || []);
      
      // Generate community groups based on colleagues' destinations
      generateCommunityGroups(data || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger la communauté"
      });
    }
  };

  const fetchMessages = async () => {
    try {
      const { data: messagesData, error } = await supabase
        .from('community_messages')
        .select('*')
        .or(`sender_id.eq.${currentProfile?.id},receiver_id.eq.${currentProfile?.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get sender profiles for each message
      if (messagesData && messagesData.length > 0) {
        const senderIds = [...new Set(messagesData.map(msg => msg.sender_id))];
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', senderIds);

        const profilesMap = profilesData?.reduce((acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        }, {} as Record<string, Profile>) || {};

        const messagesWithProfiles = messagesData.map(msg => ({
          ...msg,
          sender_profile: profilesMap[msg.sender_id] || { 
            id: msg.sender_id, 
            first_name: 'Utilisateur', 
            last_name: 'Inconnu' 
          }
        }));

        setMessages(messagesWithProfiles);
      } else {
        setMessages([]);
      }
    } catch (error: any) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedColleague || !currentProfile) return;

    try {
      const { error } = await supabase
        .from('community_messages')
        .insert({
          sender_id: currentProfile.id,
          receiver_id: selectedColleague.profiles.id,
          message: newMessage.trim()
        });

      if (error) throw error;

      toast({
        title: "Message envoyé",
        description: `Votre message a été envoyé à ${selectedColleague.profiles.first_name}`
      });

      setNewMessage("");
      setIsMessageDialogOpen(false);
      setSelectedColleague(null);
      fetchMessages();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'envoyer le message"
      });
    }
  };

  const generateCommunityGroups = (colleagues: Employee[]) => {
    const destinations = new Set<string>();
    
    // Add current employee's location if exists
    if (currentEmployee?.current_country) {
      destinations.add(currentEmployee.current_country);
    }
    
    // Add colleagues' destinations
    colleagues.forEach(colleague => {
      if (colleague.current_country) {
        destinations.add(colleague.current_country);
      }
    });

    const groups: CommunityGroup[] = Array.from(destinations).map(destination => ({
      id: `community-${destination}`,
      name: `Communauté Renest de ${destination}`,
      destination,
      memberCount: colleagues.filter(c => c.current_country === destination).length + 
                  (currentEmployee?.current_country === destination ? 1 : 0),
      isJoined: destination === currentEmployee?.current_country
    }));

    // Add some popular destinations
    const popularDestinations = ['Lisbonne', 'Beyrouth', 'Sydney', 'Bangkok', 'Dubai'];
    popularDestinations.forEach(dest => {
      if (!destinations.has(dest)) {
        groups.push({
          id: `community-${dest}`,
          name: `Communauté Renest de ${dest}`,
          destination: dest,
          memberCount: Math.floor(Math.random() * 50) + 10,
          isJoined: false
        });
      }
    });

    setCommunityGroups(groups);
  };

  const loadMockData = () => {
    const conversations: MockConversation[] = [
      {
        id: '1',
        name: 'Équipe RH',
        company: currentEmployee?.profiles ? 'Votre entreprise' : 'RH',
        location: 'France',
        duration: '',
        lastMessage: 'Votre demande de télétravail pour Bali a été approuvée ! 🎉',
        timestamp: '2h',
        isHR: true,
        avatar: 'RH'
      },
      {
        id: '2',
        name: 'Sarah Martin',
        company: 'Votre entreprise',
        location: 'Manager',
        duration: '',
        lastMessage: 'Comment se passe ton installation à Bali ?',
        timestamp: '5h',
        isManager: true,
        avatar: 'SM'
      },
      {
        id: '3',
        name: 'Anton Petrov',
        company: 'Google',
        location: 'Sydney - Depuis 1 mois',
        duration: 'Sydney - Depuis 1 mois',
        lastMessage: 'Je recommande le coworking Hub Sydney, super ambiance !',
        timestamp: '1j',
        avatar: 'AP'
      },
      {
        id: '4',
        name: 'Marie Dubois',
        company: 'Votre entreprise',
        location: 'Lisbonne - Depuis 2 semaines',
        duration: 'Lisbonne - Depuis 2 semaines',
        lastMessage: 'Tu as trouvé un bon appartement à Bali ? Je cherche pour Lisbonne',
        timestamp: '2j',
        avatar: 'MD'
      },
      {
        id: '5',
        name: 'Lucas Silva',
        company: 'Spotify',
        location: 'Bali - Depuis 3 semaines',
        duration: 'Bali - Depuis 3 semaines',
        lastMessage: 'Bienvenue à Bali ! Je peux te donner des tips sur Canggu 🏄‍♂️',
        timestamp: '3j',
        avatar: 'LS'
      }
    ];
    
    setMockConversations(conversations);
  };

  const joinCommunity = (groupId: string) => {
    setCommunityGroups(prev => 
      prev.map(group => 
        group.id === groupId 
          ? { ...group, isJoined: true, memberCount: group.memberCount + 1 }
          : group
      )
    );
    
    toast({
      title: "Communauté rejointe !",
      description: "Vous pouvez maintenant échanger avec les autres membres."
    });
  };

  const calculateDuration = (arrivalDate: string) => {
    const now = new Date();
    const arrival = new Date(arrivalDate);
    const diffTime = Math.abs(now.getTime() - arrival.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 7) {
      return `${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} semaine${weeks > 1 ? 's' : ''}`;
    } else {
      const months = Math.floor(diffDays / 30);
      return `${months} mois`;
    }
  };

  const filteredGroups = communityGroups.filter(group =>
    group.destination.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-2 text-muted-foreground">Chargement de la communauté...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="explore" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="explore" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            Explorer la communauté
          </TabsTrigger>
          <TabsTrigger value="messages" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Messages récents
          </TabsTrigger>
        </TabsList>

        <TabsContent value="explore" className="space-y-6 mt-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une destination..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Mes communautés */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Mes communautés
              </CardTitle>
              <CardDescription>
                Les communautés que vous avez rejointes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredGroups.filter(group => group.isJoined).length === 0 ? (
                <div className="text-center py-6">
                  <Users className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">Vous n'avez rejoint aucune communauté</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredGroups.filter(group => group.isJoined).map((group) => (
                    <Card key={group.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-primary/10 text-primary">
                                {group.destination.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h4 className="font-medium">{group.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {group.memberCount} membre{group.memberCount > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <Button size="sm" variant="outline">
                            <MessageCircle className="h-4 w-4 mr-2" />
                            Chat
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Communautés disponibles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Communautés disponibles
              </CardTitle>
              <CardDescription>
                Rejoignez des communautés par destination
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredGroups.filter(group => !group.isJoined).map((group) => (
                  <Card key={group.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-secondary/10 text-secondary-foreground">
                            {group.destination.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <Badge variant="secondary" className="text-xs">
                          {group.memberCount} membres
                        </Badge>
                      </div>
                      <h4 className="font-medium mb-2">{group.name}</h4>
                      <Button 
                        size="sm" 
                        className="w-full"
                        onClick={() => joinCommunity(group.id)}
                      >
                        Rejoindre
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Mes collègues */}
          {colleagues.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Mes collègues
                </CardTitle>
                <CardDescription>
                  Vos collègues dans l'entreprise
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {colleagues.slice(0, 6).map((colleague) => (
                    <Card key={colleague.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {getInitials(colleague.profiles.first_name, colleague.profiles.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium truncate">
                              {colleague.profiles.first_name} {colleague.profiles.last_name}
                            </h4>
                            {colleague.profiles.position && (
                              <p className="text-sm text-muted-foreground truncate">
                                {colleague.profiles.position}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {colleague.current_country || colleague.profiles.residence_country || 'Non défini'}
                          </span>
                        </div>
                        <Dialog open={isMessageDialogOpen && selectedColleague?.id === colleague.id} 
                                onOpenChange={(open) => {
                                  setIsMessageDialogOpen(open);
                                  if (!open) setSelectedColleague(null);
                                }}>
                          <DialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="w-full"
                              onClick={() => setSelectedColleague(colleague)}
                            >
                              <MessageCircle className="h-4 w-4 mr-2" />
                              Écrire
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>
                                Envoyer un message à {colleague.profiles.first_name}
                              </DialogTitle>
                              <DialogDescription>
                                Communiquez avec votre collègue
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <Textarea
                                placeholder="Écrivez votre message..."
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                rows={4}
                              />
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => {
                                setIsMessageDialogOpen(false);
                                setSelectedColleague(null);
                                setNewMessage("");
                              }}>
                                Annuler
                              </Button>
                              <Button onClick={sendMessage} disabled={!newMessage.trim()}>
                                <Send className="h-4 w-4 mr-2" />
                                Envoyer
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="messages" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Messages récents
              </CardTitle>
              <CardDescription>
                Vos dernières conversations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockConversations.map((conversation) => (
                  <Card key={conversation.id} className="hover:shadow-md transition-shadow cursor-pointer">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback className={`${conversation.isHR ? 'bg-blue-100 text-blue-600' : conversation.isManager ? 'bg-green-100 text-green-600' : 'bg-primary/10 text-primary'}`}>
                            {conversation.avatar}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{conversation.name}</h4>
                              {conversation.isHR && (
                                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-600">
                                  Équipe RH
                                </Badge>
                              )}
                              {conversation.isManager && (
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-600">
                                  Manager
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">{conversation.timestamp}</span>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <Building className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{conversation.company}</span>
                            {conversation.duration && (
                              <>
                                <span className="text-xs text-muted-foreground">•</span>
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">{conversation.duration}</span>
                                </div>
                              </>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{conversation.lastMessage}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CommunityTab;